<template>
  <div class="dashboard">
    <b-loading
      v-if="isLoading"
      v-model="isLoading"
      :is-full-page="false" />

    <div
      v-else-if="calendarLoadFailed"
      class="calendar-load-failed">
      <div class="calendar-load-failed-title">
        Something went wrong...
      </div>
      <div class="calendar-load-failed-body">
        This dashboard hit a snag while loading. A quick reload usually does the trick.
      </div>
      <b-button
        type="is-primary"
        @click="$router.go(0)">
        Reload page
      </b-button>
    </div>

    <div
      v-else
      class="dashboard-container"
      :class="{
        container: isContained,
      }">
      <MobileDashboardHeader
        v-if="$screen.is('sm')"
        v-model="dataContext"
        :selected-tab="selectedTab"
        :is-contained="isContained"
        class="dashboard-header"
        :dashboard-details="dashboardDetails"
        @updateTab="selectedTab = $event"
        @toggleContainer="toggleContainer" />

      <DashboardHeader
        v-else
        v-model="dataContext"
        :selected-tab="selectedTab"
        :is-contained="isContained"
        class="dashboard-header"
        :dashboard-details="dashboardDetails"
        @updateTab="selectedTab = $event"
        @toggleContainer="toggleContainer"
        @refreshDashboard="$router.go(0)" />

      <div class="dashboard-content">
        <div
          v-for="section in dashboardDetails.dashboard_sections"
          :key="section.id">
          <DashboardContent
            v-if="section.id === selectedTab"
            :is-ready="isReady"
            :dashboard-data-context="dataContext"
            :dashboard-key="dashboardKey"
            :dashboard-section="section"
            :shared-modules-map="sharedModulesMap"
            @module-shared="handleModuleShared"
            @module-unshared="handleModuleUnshared" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import DashboardHeader from './components/DashboardHeader.vue'
  import MobileDashboardHeader from './components/MobileDashboardHeader.vue'

  import DashboardContent from './components/DashboardContent.vue'

  export default {
    components: {
      DashboardHeader,
      MobileDashboardHeader,

      DashboardContent,
    },
    provide () {
      return {
        isContained: () => this.isContained,
      }
    },
    data () {
      return {
        isContained: true,
        isLoading: true,
        selectedTab: '',
        dashboardDetails: null,
        dashboardKey: null,
        dataContext: {
          date_selection: {
            selected_dates: [],
          },
          filter_selections: {},
          control_selections: {},
          debug: this.$route.query.debug || false,
        },
        sharedModulesMap: {},
      }
    },
    computed: {
      isReady () {
        for (let requiredFilter of this.dashboardDetails?.required_filters) {
          if (!this.dataContext.filter_selections[requiredFilter.key]) {
            return false
          }
        }
        for (let dashboardControl of this.dashboardDetails?.dashboard_controls) {
          if (!this.dataContext.control_selections[dashboardControl.key]) {
            return false
          }
        }
        return true
      },
      variant () {
        return this.$screen.is('sm') ? 'mobile' : 'desktop'
      },
      calendarLoadFailed () {
        return this.$store.getters['datePicker/loadFailed']
      },
    },
    watch: {
      variant () {
        this.loadDashboard(this.dashboardKey)
      },
    },
    mounted () {
      this.dashboardKey = this.$route.params.id
      this.loadDashboard(this.dashboardKey)
    },
    methods: {
      toggleContainer () {
        this.isContained = !this.isContained
      },
      handleModuleShared (event) {
        this.$set(this.sharedModulesMap, event.moduleKey, event.sharedModuleId)
      },
      handleModuleUnshared (event) {
        this.$delete(this.sharedModulesMap, event.moduleKey)
      },
      async loadDashboard (dashboardKey) {
        this.isLoading = true
        this.dataContext = {
          date_selection: {
            selected_dates: [],
          },
          filter_selections: {},
          control_selections: {},
          debug: this.$route.query.debug || false,
        }
        try {
          const dashboardDetails =
            await this.$apis.companyConfigs.getDashboardReportDetails(
              dashboardKey
            )
          this.dashboardDetails = dashboardDetails
          // Fetch shared modules for this dashboard
          try {
            const sharedModules = await this.$apis.companyConfigs.listSharedModules()
            const map = {}
            if (Array.isArray(sharedModules)) {
              sharedModules
                .filter(m => m.dashboard_report_key === dashboardKey)
                .forEach(m => { map[m.dashboard_module_key] = m.id })
            }
            this.sharedModulesMap = map
          } catch {
            this.sharedModulesMap = {}
          }
          this.selectedTab = this.dashboardDetails?.dashboard_sections[0]?.id
          this.$tracker.capture('dashboard_viewed', {
            dashboard_id: this.dashboardDetails.id,
            dashboard_key: this.dashboardDetails.key,
            dashboard_name: this.dashboardDetails.name,
          })
        } finally {
          this.isLoading = false
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
.dashboard {
  padding: 16px;
  background-color: #e9e9ea;

  @include mobile {
    padding: 0px;
  }
}

.dashboard-container {
  padding: 0 !important;
  &.container {
    max-width: 1400px !important;
    margin: 0 auto;
    position: static;
  }
}
.calendar-load-failed {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  max-width: 420px;
  margin: 64px auto;
  padding: 0 16px;
  text-align: center;
}
.calendar-load-failed-title {
  font-size: 18px;
  font-weight: 600;
}
.calendar-load-failed-body {
  margin-bottom: 8px;
}
.dashboard-header {
  @include mobile {
    margin: 0;
  }
}
</style>
